import { useCallback, useState, useEffect } from 'react';
import { GridReadyEvent, ICellRendererParams } from 'ag-grid-community';
import CommonButton from '../../../components/common-components/button/CommonButton';
import styles from './HospitalManagement.module.css';
import toolbarStyles from '../../../styles/ToolbarButtons.module.css';
import { Pencil, Trash2, Plus, ArrowRight, RotateCw, FileSpreadsheet } from 'lucide-react';
import { useStore } from '../../../hooks/useStore';
import { observer } from 'mobx-react-lite';
import { IHospitalResult, ITag } from '../../../models/hospital/HospitalModel';
import NewHospital from '../hospital-files/parts/new-data/new-hospital/NewHospital';
import DeleteConfirmationDialog from '../hospital-files/parts/delete/DeleteConfirmationDialog';
import NewTagNumber from '../hospital-files/parts/new-data/new-tag/NewTagNumber';
import EditHospital from '../hospital-files/parts/edit-data/EditHospital';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import Popup from '../../../components/common-components/pop-up/Popup';
import CommonDropdown from '../../../components/common-components/dropdown/CommonDropdown';
import { hospitalService } from '../../../services/modules/hospital/hospital.service';
import { MultiSelect } from '../../../components/common-components/multiselect/CommonMultiselect';
import * as XLSX from 'xlsx';
import BaseAgGrid from '../../../components/common-components/grid/BaseAgGrid';
import {
  useHospitalManagementColumnDefs,
  useHospitalManagementDefaultColDef,
} from './HospitalManagement.grid';

const ActionCellRenderer = (props: ICellRendererParams<IHospitalResult>) => {
  const { popupStore, hospitalStore, authStore } = useStore();
  const isAdmin = authStore.isAdmin;

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (props.data) {
      hospitalStore.setSelectedHospitalToEdit(props.data);
      popupStore.setIsEditHospitalOpen(true);
    }
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isAdmin) {
      toast.error('Bu işlem için admin yetkisi gereklidir.');
      return;
    }
    if (props.data) {
      hospitalStore.setSelectedHospitalToDelete(props.data);
      popupStore.setIsDeleteConfirmationOpen(true);
    }
  };

  const handleSelectTags = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (props.data) {
      hospitalStore.setSelectedHospitalForTags(props.data);
      popupStore.setIsSelectTagsOpen(true);
    }
  };

  return (
    <div className={styles.actionCell}>
      <button onClick={handleEdit} className={styles.editButton} title="Düzenle">
        <Pencil size={20} />
      </button>

      <button
        onClick={handleDelete}
        className={`${styles.deleteButton} ${!isAdmin ? styles.disabledButton : ''}`}
        title={isAdmin ? 'Sil' : 'Bu işlem için admin yetkisi gereklidir'}
        disabled={!isAdmin}
      >
        <Trash2 size={20} />
      </button>
      <button onClick={handleSelectTags} className={styles.selectButton} title="Etiket Seç">
        <ArrowRight size={20} />
      </button>
    </div>
  );
};

const SelectTagsPopup = () => {
  const { popupStore, hospitalStore, hospitalFilesStore } = useStore();
  const navigate = useNavigate();
  const [selectedTag, setSelectedTag] = useState<string>('');
  const [loadedTags, setLoadedTags] = useState<ITag[]>([]);
  const [tags, setTags] = useState<Array<{ value: string; label: string }>>([]);
  const [loading, setLoading] = useState(false);

  const selectedHospitalId = hospitalStore.selectedHospitalForTags?.id;

  const loadHospitalTags = useCallback(async () => {
    if (!selectedHospitalId) return;

    setLoading(true);
    try {
      const response = await hospitalService.getTagsByHospitalId(selectedHospitalId);

      if (response && Array.isArray(response)) {
        setLoadedTags(response);
        const tagOptions = response.map((tag: ITag) => ({
          value: tag.id.toString(),
          label: `${tag.prefix}`,
        }));
        setTags(tagOptions);
      } else {
        setLoadedTags([]);
        setTags([]);
        toast.warning('Bu hastane için etiket bulunamadı');
      }
    } catch (error) {
      console.error('Etiketler yüklenirken hata:', error);
      toast.error('Etiketler yüklenirken bir hata oluştu');
      setLoadedTags([]);
      setTags([]);
    } finally {
      setLoading(false);
    }
  }, [selectedHospitalId]);

  useEffect(() => {
    if (popupStore.isSelectTagsOpen && selectedHospitalId) {
      loadHospitalTags();
    }
  }, [popupStore.isSelectTagsOpen, selectedHospitalId, loadHospitalTags]);

  const handleNavigate = () => {
    if (!selectedTag) {
      toast.error('Lütfen bir etiket seçin');
      return;
    }

    if (!hospitalStore.selectedHospitalForTags) {
      toast.error('Hastane bilgisi bulunamadı');
      return;
    }

    // Hospital Files store'unda hastane ve etiket seç
    hospitalFilesStore.setSelectedHospital(hospitalStore.selectedHospitalForTags);

    const foundTag = loadedTags.find(tag => tag.id.toString() === selectedTag);
    if (foundTag) hospitalFilesStore.setSelectedTag(foundTag);

    // Hospital Files sayfasına yönlendir
    navigate('/hospital-files');
    popupStore.setIsSelectTagsOpen(false);
    setSelectedTag('');
  };

  const handleClose = () => {
    popupStore.setIsSelectTagsOpen(false);
    setSelectedTag('');
    hospitalStore.setSelectedHospitalForTags(null);
  };

  return (
    <Popup
      isOpen={popupStore.isSelectTagsOpen}
      onClose={handleClose}
      title="Etiket Seç"
      footer={
        <CommonButton type="primary" onClick={handleNavigate} disabled={!selectedTag || loading}>
          Git
        </CommonButton>
      }
    >
      <div className={styles.selectTagsContainer}>
        <div className={styles.hospitalInfo}>
          <h4>Hastane: {hospitalStore.selectedHospitalForTags?.hospitalName}</h4>
        </div>

        <div className={styles.dropdownContainer}>
          <label className={styles.dropdownLabel}>Etiket Seçin:</label>
          <CommonDropdown
            options={tags}
            value={selectedTag}
            onChange={value => setSelectedTag(value || '')}
            placeholder={loading ? 'Yükleniyor...' : 'Etiket seçin...'}
            disabled={loading}
          />
        </div>
      </div>
    </Popup>
  );
};

const ExportExcelPopup = () => {
  const { popupStore, hospitalStore, hospitalFilesStore, uiStore } = useStore();
  const [selectedHospitals, setSelectedHospitals] = useState<string[]>([]);
  const [hospitals, setHospitals] = useState<Array<{ value: string; label: string }>>([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const loadAllHospitals = useCallback(async () => {
    setLoading(true);
    try {
      const result = await hospitalStore.listAllHospitals();
      if (result && result.rows.length > 0) {
        const hospitalOptions = result.rows
          .filter((hospital: IHospitalResult) => hospital.hospitalName)
          .map((hospital: IHospitalResult) => ({
            value: hospital.id.toString(),
            label: hospital.hospitalName || '',
          }));
        setHospitals(hospitalOptions);
      } else {
        setHospitals([]);
        toast.warning('Hastane bulunamadı');
      }
    } catch (error) {
      console.error('Hastaneler yüklenirken hata:', error);
      toast.error('Hastaneler yüklenirken bir hata oluştu');
      setHospitals([]);
    } finally {
      setLoading(false);
    }
  }, [hospitalStore]);

  useEffect(() => {
    if (popupStore.isExportExcelOpen) {
      loadAllHospitals();
    }
  }, [popupStore.isExportExcelOpen, loadAllHospitals]);

  const handleExport = async () => {
    if (selectedHospitals.length === 0) {
      toast.error('Lütfen en az bir hastane seçin');
      return;
    }

    setExporting(true);
    uiStore.setLoading(true);

    try {
      const originalHospital = hospitalFilesStore.selectedHospital;
      const allExportData: any[] = [];

      // Seçilen tüm hastaneler için veri çek ve birleştir
      for (const hospitalId of selectedHospitals) {
        const hospital = hospitals.find(h => h.value === hospitalId);
        if (!hospital) continue;

        // Hastane bilgisini bul
        const hospitalResult = await hospitalStore.listAllHospitals();
        const hospitalData = hospitalResult.rows.find(
          (h: IHospitalResult) => h.id.toString() === hospitalId
        );

        if (!hospitalData) continue;

        // Geçici olarak selectedHospital'ı set et
        hospitalFilesStore.setSelectedHospital(hospitalData);

        try {
          // Hastane kayıtlarını çek
          const result = await hospitalFilesStore.listHospitalRecord({
            startRow: 0,
            endRow: 1000000,
            filterModel: null,
            sortModel: null,
          });

          if (result?.rows.length) {
            const exportData = result.rows.map(item => ({
              ID: item.id,
              Hastane: item.hospitalName,
              'Cihaz Tanımı': item.biomedicalDeviceDescription,
              Marka: item.brand,
              Model: item.model,
              'Seri No': item.serialNumber,
              'Künye No': item.identificationNumber,
              Lokasyon: item.location,
              'Etiket No': item.tagNumberWithColor,
              'Eklenme Tarihi': new Date(item.addDate).toLocaleDateString('tr-TR'),
              'Zimmetli Kişi': item.responsiblePerson,
              Notlar: item.note,
              'Ekleyen Kişi': item.createdBy,
            }));

            allExportData.push(...exportData);
          }
        } catch (error) {
          console.error(`Hastane ${hospital.label} için veri çekme hatası:`, error);
          toast.warning(`${hospital.label} için veri çekilemedi`);
        }
      }

      // Orijinal hastane bilgisini geri yükle
      hospitalFilesStore.setSelectedHospital(originalHospital);

      if (allExportData.length === 0) {
        toast.error('Seçilen hastaneler için veri bulunamadı');
        return;
      }

      // Tüm verileri tek bir Excel dosyasına aktar
      const ws = XLSX.utils.json_to_sheet(allExportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Hastane Cihazları');

      const dateStr = new Date().toISOString().split('T')[0];
      const selectedHospitalNames = selectedHospitals
        .map(id => {
          const hospital = hospitals.find(h => h.value === id);
          return hospital?.label || '';
        })
        .filter(Boolean)
        .join('_');

      const sanitizedNames = selectedHospitalNames
        .replace(/[ıİ]/g, 'I')
        .replace(/[ğĞ]/g, 'G')
        .replace(/[üÜ]/g, 'U')
        .replace(/[şŞ]/g, 'S')
        .replace(/[öÖ]/g, 'O')
        .replace(/[çÇ]/g, 'C')
        .replace(/[^a-zA-Z0-9]/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_|_$/g, '');

      const fileName =
        selectedHospitals.length === 1
          ? `${sanitizedNames}_Hastane_Cihazlari_${dateStr}.xlsx`
          : `Hastane_Cihazlari_${dateStr}.xlsx`;

      XLSX.writeFile(wb, fileName);

      toast.success(
        `${selectedHospitals.length} hastane için ${allExportData.length} kayıt tek bir Excel dosyasına aktarıldı`
      );
      popupStore.setIsExportExcelOpen(false);
      setSelectedHospitals([]);
    } catch (error) {
      console.error('Excel export error:', error);
      toast.error('Excel export sırasında bir hata oluştu');
    } finally {
      setExporting(false);
      uiStore.setLoading(false);
    }
  };

  const handleClose = () => {
    popupStore.setIsExportExcelOpen(false);
    setSelectedHospitals([]);
  };

  return (
    <Popup
      isOpen={popupStore.isExportExcelOpen}
      onClose={handleClose}
      title="Excel'e Aktar"
      footer={
        <CommonButton
          type="primary"
          onClick={handleExport}
          disabled={selectedHospitals.length === 0 || loading || exporting}
        >
          {exporting ? 'Aktarılıyor...' : "Excel'e Aktar"}
        </CommonButton>
      }
    >
      <div className={styles.selectTagsContainer}>
        <div className={styles.dropdownContainer}>
          <label className={styles.dropdownLabel}>Hastaneleri Seçin:</label>
          <MultiSelect
            options={hospitals}
            value={selectedHospitals}
            onChange={setSelectedHospitals}
            placeholder={loading ? 'Yükleniyor...' : 'Hastane seçin...'}
            disabled={loading}
          />
        </div>
      </div>
    </Popup>
  );
};

const HospitalManagement = () => {
  const { popupStore, hospitalStore } = useStore();

  const onGridReady = useCallback(
    (params: GridReadyEvent<IHospitalResult>) => {
      hospitalStore.setGridApi(params.api);
    },
    [hospitalStore]
  );

  const columnDefs = useHospitalManagementColumnDefs(ActionCellRenderer);
  const defaultColDef = useHospitalManagementDefaultColDef();

  const handleNewHospital = () => {
    popupStore.setIsNewHospitalOpen(true);
  };

  const handleNewTagNumber = () => {
    popupStore.setIsNewTagOpen(true);
  };

  const handleExportExcel = () => {
    popupStore.setIsExportExcelOpen(true);
  };

  return (
    <>
      <div className={styles.container}>
        <div className={styles.gridContainer}>
          <BaseAgGrid<IHospitalResult>
            fetchRows={params => hospitalStore.listAllHospitals(params)}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            onGridReady={onGridReady}
            title="Hastane Yönetimi"
            toolbarLeft={({ refresh }) => (
              <>
                <CommonButton
                  type="primary"
                  onClick={handleNewHospital}
                  className={styles.toolbarBtnPrimary}
                >
                  <Plus size={18} />
                  Yeni Hastane Oluştur
                </CommonButton>
                <CommonButton
                  type="outline"
                  onClick={handleNewTagNumber}
                  className={styles.toolbarBtnTag}
                >
                  Hastane Etiket No Ekle
                </CommonButton>
                <CommonButton
                  type="tertiary"
                  onClick={handleExportExcel}
                  leftIcon={<FileSpreadsheet size={18} />}
                  className={toolbarStyles.toolbarBtnExcel}
                >
                  Excel'e Aktar
                </CommonButton>
                <CommonButton
                  type="refresh"
                  onClick={refresh}
                  leftIcon={<RotateCw size={18} />}
                  className={toolbarStyles.toolbarBtnRefresh}
                >
                  Yenile
                </CommonButton>
              </>
            )}
            getRowStyle={params =>
              params.data?.isHospitalClosed === true ? { backgroundColor: '#fef2f2' } : undefined
            }
          />
        </div>
      </div>
      {popupStore.isNewHospitalOpen && <NewHospital />}
      {popupStore.isNewTagOpen && <NewTagNumber />}
      {popupStore.isEditHospitalOpen && <EditHospital />}
      <DeleteConfirmationDialog
        isOpen={popupStore.isDeleteConfirmationOpen}
        onClose={() => popupStore.setIsDeleteConfirmationOpen(false)}
        onConfirm={async () => {
          if (hospitalStore.selectedHospitalToDelete?.id) {
            await hospitalStore.deleteHospital(hospitalStore.selectedHospitalToDelete.id);
            hospitalStore.refreshGrid();
          }
          popupStore.setIsDeleteConfirmationOpen(false);
        }}
        title={`Hastane Silme Onayı`}
      />
      {popupStore.isSelectTagsOpen && <SelectTagsPopup />}
      {popupStore.isExportExcelOpen && <ExportExcelPopup />}
    </>
  );
};

export default observer(HospitalManagement);
